import { useState } from 'react';
import { formResponses } from '../../../mocks/leadCapture';
import type { FormResponse, ResponseStatus, CaptureForm } from '../../../mocks/leadCapture';

interface Props {
  forms: CaptureForm[];
  filterFormId?: string | null;
  onConvertToLead: (response: FormResponse) => void;
}

const statusConfig: Record<ResponseStatus, { label: string; color: string; bg: string }> = {
  New: { label: 'New', color: '#0ea5e9', bg: 'bg-sky-50 text-sky-700' },
  Reviewed: { label: 'Reviewed', color: '#f59e0b', bg: 'bg-amber-50 text-amber-700' },
  Converted: { label: 'Converted', color: '#10b981', bg: 'bg-emerald-50 text-emerald-700' },
  Dismissed: { label: 'Dismissed', color: '#94a3b8', bg: 'bg-gray-100 text-gray-500' },
};

const deviceIcons: Record<string, string> = {
  Mobile: 'ri-smartphone-line',
  Desktop: 'ri-computer-line',
  Tablet: 'ri-tablet-line',
};

export default function ResponsesInbox({ forms, filterFormId, onConvertToLead }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ResponseStatus | 'All'>('All');
  const [formFilter, setFormFilter] = useState<string>(filterFormId || 'All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [localStatuses, setLocalStatuses] = useState<Record<string, ResponseStatus>>({});

  const getStatus = (r: FormResponse): ResponseStatus => localStatuses[r.id] || r.status;

  const filtered = formResponses.filter((r) => {
    const name = Object.values(r.data).find((v) => v.length < 60 && v.includes(' ')) || '';
    const matchSearch =
      Object.values(r.data).some((v) => v.toLowerCase().includes(search.toLowerCase())) ||
      r.formName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || getStatus(r) === statusFilter;
    const matchForm = formFilter === 'All' || r.formId === formFilter;
    return matchSearch && matchStatus && matchForm;
  });

  const counts: Record<string, number> = { All: formResponses.length };
  (['New', 'Reviewed', 'Converted', 'Dismissed'] as ResponseStatus[]).forEach((s) => {
    counts[s] = formResponses.filter((r) => getStatus(r) === s).length;
  });

  const markStatus = (id: string, status: ResponseStatus) => {
    setLocalStatuses((prev) => ({ ...prev, [id]: status }));
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search submissions by name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300"
          />
        </div>
        <select
          value={formFilter}
          onChange={(e) => setFormFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300 whitespace-nowrap"
        >
          <option value="All">All Forms</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {/* Status filters */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {(['All', 'New', 'Reviewed', 'Converted', 'Dismissed'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === s
                ? 'bg-gray-800 text-white border-gray-800'
                : 'text-gray-500 border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            {s} <span className="opacity-60">({counts[s] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Responses list */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-inbox-2-line text-4xl block mb-2 text-gray-200" />
            <p className="text-sm">No submissions match your filters</p>
          </div>
        )}
        {filtered.map((resp) => {
          const status = getStatus(resp);
          const sc = statusConfig[status];
          const entries = Object.entries(resp.data);
          const nameEntry = entries.find(([k]) => k.toLowerCase().includes('name')) || entries[0];
          const emailEntry = entries.find(([k]) => k.toLowerCase().includes('email'));
          const phoneEntry = entries.find(([k]) => k.toLowerCase().includes('phone') || k.toLowerCase().includes('whatsapp'));
          const isExpanded = expandedId === resp.id;

          return (
            <div key={resp.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {/* Row header */}
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : resp.id)}
              >
                {/* Avatar */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: resp.formColor }}
                >
                  {(nameEntry?.[1] || '?').slice(0, 2).toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-800">{nameEntry?.[1] || '—'}</p>
                    {status === 'New' && (
                      <span className="w-2 h-2 rounded-full bg-sky-500 inline-block flex-shrink-0" />
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.bg}`}>{sc.label}</span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: `${resp.formColor}18`, color: resp.formColor }}
                    >
                      {resp.formName}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                    {emailEntry && <span>{emailEntry[1]}</span>}
                    {phoneEntry && <span>{phoneEntry[1]}</span>}
                    <span className="flex items-center gap-1"><i className={deviceIcons[resp.device]} />{resp.device}</span>
                    <span>{resp.ipCountry}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-400 hidden sm:block whitespace-nowrap">{resp.submittedAt}</span>
                  <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-gray-400`} />
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 mb-4">
                    {entries.map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-0.5">{key}</p>
                        <p className="text-sm text-gray-800 font-medium break-words">{value || '—'}</p>
                      </div>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1"><i className="ri-time-line" />{resp.submittedAt}</span>
                    <span className="flex items-center gap-1"><i className={deviceIcons[resp.device]} />{resp.device}</span>
                    <span className="flex items-center gap-1"><i className="ri-global-line" />{resp.source}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line" />{resp.ipCountry}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {status !== 'Converted' && (
                      <button
                        onClick={() => { onConvertToLead(resp); markStatus(resp.id, 'Converted'); }}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs text-white font-medium rounded-lg cursor-pointer whitespace-nowrap"
                        style={{ backgroundColor: '#10b981' }}
                      >
                        <i className="ri-user-add-line" />
                        Convert to Lead
                      </button>
                    )}
                    {status === 'New' && (
                      <button
                        onClick={() => markStatus(resp.id, 'Reviewed')}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-eye-line" />
                        Mark Reviewed
                      </button>
                    )}
                    {status !== 'Dismissed' && status !== 'Converted' && (
                      <button
                        onClick={() => markStatus(resp.id, 'Dismissed')}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-close-line" />
                        Dismiss
                      </button>
                    )}
                    {resp.leadId && (
                      <div className="flex items-center gap-1.5 ml-auto text-xs text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
                        <i className="ri-user-follow-line" />
                        Converted → Lead {resp.leadId}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mt-4 text-right">Showing {filtered.length} of {formResponses.length} submissions</p>
    </div>
  );
}
